import {
  del as idbDel,
  delMany as idbDelMany,
  get as idbGet,
  keys as idbKeys,
  set as idbSet,
  type UseStore,
} from "idb-keyval";

/**
 * IndexedDB can be entirely unavailable or throw on access in some environments:
 * Safari private mode and — most importantly for our marketing-embed ad traffic —
 * in-app webviews (Instagram / Facebook browsers). In those environments `open`
 * can fail, or (FRONTEND-1J0) the `onupgradeneeded` handler fires with a null
 * `request.result`, causing `result.createObjectStore(...)` to throw an uncaught
 * error from inside the event handler.
 *
 * This wrapper mirrors idb-keyval's default store but:
 *   1. Guards the `onupgradeneeded` handler so it can never throw.
 *   2. Falls back to an in-memory `Map` on any failure (sync throw or async
 *      rejection) so callers keep working for the session.
 *
 * The DB/store names match idb-keyval's defaults ("keyval-store" / "keyval") so
 * any previously-persisted data is still read where IndexedDB does work.
 */

const memory = new Map<string, unknown>();
let blocked = false;

const idbAvailable = () => typeof indexedDB !== "undefined";

/**
 * `onblocked` is handled as well as success and error, and that is not a nicety.
 *
 * A blocked open fires NEITHER of the other two: the request simply sits there while another
 * connection holds the database. Unhandled, the promise never settles — and a caller awaiting it
 * waits forever rather than failing over to memory. That is reachable here: the desktop app runs
 * several webviews against one origin, and a second one opening this store while the first is
 * mid-upgrade is exactly the race the event exists to report.
 */
function promisifyRequest<T>(request: IDBRequest<T> | IDBOpenDBRequest): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    request.onsuccess = () => resolve(request.result as T);
    request.onerror = () => reject(request.error);
    if ("onblocked" in request) {
      request.onblocked = () => reject(new Error("IndexedDB open was blocked by another connection"));
    }
  });
}

let customStore: UseStore | null = null;

function getStore(): UseStore {
  if (customStore) return customStore;
  let dbp: Promise<IDBDatabase> | undefined;
  const getDB = () => {
    if (dbp) return dbp;
    const request = indexedDB.open("keyval-store");
    request.onupgradeneeded = () => {
      // In some webviews `request.result` is null during the upgrade — guard so
      // the event handler never throws an uncaught error (FRONTEND-1J0). If the
      // object store isn't created, the later transaction rejects and we fall
      // back to the in-memory store.
      try {
        request.result?.createObjectStore("keyval");
      } catch {
        // ignore
      }
    };
    dbp = promisifyRequest<IDBDatabase>(request);
    dbp.then(
      (db) => {
        // Safari sometimes closes the connection out from under us.
        db.onclose = () => {
          dbp = undefined;
        };
      },
      () => {}
    );
    return dbp;
  };
  customStore = ((txMode, callback) =>
    getDB().then((db) => callback(db.transaction("keyval", txMode).objectStore("keyval")))) as UseStore;
  return customStore;
}

export async function idbGetSafe<T>(key: string): Promise<T | undefined> {
  if (blocked || !idbAvailable()) {
    return memory.get(key) as T | undefined;
  }
  try {
    return await idbGet<T>(key, getStore());
  } catch {
    blocked = true;
    return memory.get(key) as T | undefined;
  }
}

export async function idbSetSafe(key: string, value: unknown): Promise<void> {
  if (blocked || !idbAvailable()) {
    memory.set(key, value);
    return;
  }
  try {
    await idbSet(key, value, getStore());
  } catch {
    blocked = true;
    memory.set(key, value);
  }
}

export async function idbDelSafe(key: string): Promise<void> {
  if (blocked || !idbAvailable()) {
    memory.delete(key);
    return;
  }
  try {
    await idbDel(key, getStore());
  } catch {
    blocked = true;
    memory.delete(key);
  }
}

/**
 * Drop every entry whose key starts with `prefix`.
 *
 * The per-query persister only checks an entry's age when something reads it, so rows belonging to
 * a query that has since opted out of persistence are never visited again and stay on disk for
 * good. This is the sweep for those.
 */
export async function idbDeleteByPrefixSafe(prefix: string): Promise<number> {
  if (blocked || !idbAvailable()) {
    const stale = [...memory.keys()].filter((key) => key.startsWith(prefix));
    for (const key of stale) memory.delete(key);
    return stale.length;
  }
  try {
    const store = getStore();
    const stale = (await idbKeys(store)).filter(
      (key): key is string => typeof key === "string" && key.startsWith(prefix)
    );
    if (stale.length) await idbDelMany(stale, store);
    return stale.length;
  } catch {
    blocked = true;
    return 0;
  }
}
